import React from 'react';
import { EnumHelper, ModalUtils, NotifyUtils } from '@singularsystems/neo-core';
import { Neo, NeoGrid } from '@singularsystems/neo-react';
import PortfolioVM from '../PortfolioVM';
import { BalancesGroupedGrid } from './BalancesGroupedGrid';
import { PendingTradeRequestsComponent } from '../../Components/TradeRequests/PendingTradeRequestsComponent';
import { VestingType } from '../../../Models/Portfolio/IncentiveGroup';
import { observer } from 'mobx-react';
import { HtmlView } from '@singularsystems/neo-notifications';
import { TemplateLink } from './TemplateLink';
import MinShareholdingSummary from "./MinShareholdingSummary";
import PortfolioSummary from "./PortfolioSummary";
import { NotificationDuration } from 'st-app/Models/Enums/NotificationDuration';

interface IBalancesComponentProps {
    viewModel: PortfolioVM;
    onCalculateClick: (forTrading: boolean) => void;
}

@observer
export class BalancesComponent extends React.Component<IBalancesComponentProps> {

    private showCalculator() {
        this.props.onCalculateClick(false);
    }

    private showTradeCalculator() {
        if (this.props.viewModel.vestingType === VestingType.UnvestedOnly) {
            ModalUtils.showMessage("Calculator", "Please change the vesting type filter to continue.");
        } else {
            const hasUnvested = this.props.viewModel.incentiveGroups.some(c => c.groupedList.some(a => a.isSelected && a.vestedBalance === 0));
            const withVested = this.props.viewModel.incentiveGroups.filter(c => c.groupedList.some(a => a.isSelected && a.vestedBalance > 0));
  
            if (withVested.length === 0) {
                if (hasUnvested) {
                    ModalUtils.showMessage("Calculator", "The selected awards are not vested, and cannot be traded. Please select an award with vested units.");
                } else {
                    ModalUtils.showMessage("Calculator", "Please select one or more awards before clicking continue.");
                }
                
            } else {
                if (hasUnvested) {
                    NotifyUtils.addInfo("Calculator", "Some of the selected awards are not vested and will be ignored for trading.", NotificationDuration.Long);
                }
                this.props.onCalculateClick(true);
            }
        }
    }

    public render() {
        const viewModel = this.props.viewModel,
            portfolioData = viewModel.portfolioData,
            exchangeRateService = viewModel.portfolioService.exchangeRateService,
            noAwards = portfolioData && portfolioData.trancheBalances.length === 0;

        const showPortfolioSummary = portfolioData?.settings.showParticipantPortfolioSummary ?? true;

        return (
            <div>
                <h2>My Portfolio Details</h2>
                
                {viewModel.minimumShareholdingSummary &&
                    <MinShareholdingSummary minimumShareholdingSummary={viewModel.minimumShareholdingSummary} />}

                {noAwards &&
                    <div className="mt-4">
                        You do not have any awards yet.
                    </div>}

                {!noAwards &&
                    <div>
                        <div className="my-3 portfolio-note">
                            <HtmlView html={viewModel.portfolioNote} />
                            <div className="mt-2">
                                <TemplateLink templateTypeKey="TradeProcessExplanation">
                                    Click here to find out more about the trade process.
                                </TemplateLink>
                            </div>
                        </div>

                        {showPortfolioSummary && 
                            <PortfolioSummary viewModel={viewModel.portfolioSummaryVM} />}

                        {portfolioData && exchangeRateService &&
                            <>
                                <div className="flex-container flex-v-end">
                                    {viewModel.calendarYearFilterEnabled && viewModel.yearBucketDropdownItems.length > 0 ? (
                                        <Neo.FormGroup label="Show portfolio by:" bind={viewModel.meta.yearBucket}
                                            select={{ items: viewModel.yearBucketDropdownItems }} editorClassName="highlight"
                                            editorStyle={{ width: 220, paddingRight: 0 }} />
                                    ) : (
                                        <Neo.FormGroup label="Show portfolio by:" bind={viewModel.meta.vestingType}
                                            select={{ items: EnumHelper.asList(VestingType) }} editorClassName="highlight"
                                            editorStyle={{ width: 180, paddingRight: 0 }} />
                                    )}

                                    <div style={{ flex: "auto", display: "flex", alignItems: "flex-end", justifyContent: "flex-end", textAlign: "right" }}>
                                        {portfolioData && portfolioData.settings.allowParticipantPortfolioDownload &&
                                            <Neo.Button
                                                className="btn-150 mb-3 ms-3 d-none d-sm-inline"
                                                onClick={async () => await viewModel.downloadParticipantStatement()}
                                                tooltip={"Downloads a PDF statement of the portfolio."}>Download</Neo.Button>}

                                        {portfolioData &&
                                            <Neo.Button
                                                className="btn-150"
                                                buttonGroupClassName="mb-3 ms-3 d-none d-sm-inline calculate-button"
                                                menuAlignment='right'
                                                menuItems={[
                                                    { text: <><div>Edit prices</div><span className="text-muted">Edit instrument prices on the current screen.</span></>, onClick: () => viewModel.portfolioData!.instrumentEditMode = !viewModel.portfolioData?.instrumentEditMode },
                                                    { text: <><div>Calculator</div><span className="text-muted">Enter the advanced calculator to see what the value of your awards could be in different scenarios.</span></>, onClick: () => this.showCalculator() },
                                                    { text: <><div>Trade</div><span className="text-muted">Begin trading the selected items below.</span></>, onClick: () => this.showTradeCalculator() },
                                                    ...((!showPortfolioSummary || !viewModel.portfolioSummaryVM.valuesByYear || !viewModel.portfolioSummaryVM.portfolioData) ? [{
                                                        text: <><div>{viewModel.portfolioSummaryVM.payoutRatioTrackingUnits ? "Remove Payout ratio" : "Apply Payout ratio"}</div><span className="text-muted">The Payout ratio tracking % will be applied to the Total Units column.</span></>,
                                                        onClick: () => { viewModel.portfolioSummaryVM.payoutRatioTrackingUnits = !viewModel.portfolioSummaryVM.payoutRatioTrackingUnits; }
                                                    }] : [])
                                                ]}
                                            >Calculate</Neo.Button>}


                                        {exchangeRateService.showMissingExchangeRateWarning &&
                                            <Neo.Alert className="alert-tiny ms-3 mb-3" variant="warning" heading="Missing Exchange rates">
                                                Awards that cannot be converted will be shown in the award currency.
                                            </Neo.Alert>}

                                        <Neo.FormGroup
                                            className="ms-3 mb-3"
                                            editorClassName="highlight"
                                            suppressLabel
                                            editorStyle={{ width: exchangeRateService.currencyId ? 100 : 200 }}
                                            bind={exchangeRateService.meta.currencyId}
                                            select={{ items: viewModel.portfolioData?.mainCurrencies, allowNulls: true, nullText: "Show in Unit currency", deSelectText: "Show in Unit currency" }} />
                                    </div>
                                </div>

                                {viewModel.portfolioSummaryVM.payoutRatioTrackingUnits &&
                                    <Neo.Alert className="alert-tiny mb-3" variant="warning">
                                        <i className="fa fa-exclamation-circle me-1" />
                                        Payout ratio tracking (<i className="fa fa-arrow-trend-up payout-ratio-icon scale-09" data-tip="Payout ratio % is enabled for this award"/>) is applied. The Total Units column will now display unit balance adjusted by the payout ratio % applied rounded down.
                                    </Neo.Alert>}

                                <Neo.Loader show={exchangeRateService.exchangeRatesTask.isBusy}>
                                    {viewModel.incentiveGroups.map(group => (
                                        <div key={group.entityIdentifier} className="portfolio-group-container">
                                            <div className="portfolio-group-header">
                                                <div className="portfolio-group-title">
                                                    {group.info.groupName}
                                                </div>
                                            </div>
                                            <div className="portfolio-group-body overflow-x-auto-mobile">
                                                {portfolioData &&
                                                    <BalancesGroupedGrid category={group}
                                                        vestingType={viewModel.effectiveVestingType}
                                                        editMode={portfolioData.instrumentEditMode} 
                                                        hasCostsAlreadyPaidGroups={viewModel.portfolioService.appData?.hasCostsAlreadyPaidGroups? true : false}
                                                        applyPayoutRatio={viewModel.portfolioSummaryVM.payoutRatioTrackingUnits}
                                                    />}
                                            </div>
                                            {group.info.userDescription &&
                                                <div className="portfolio-group-header py-2 mb-2">
                                                    <small className="text-heading pre-wrap">{group.info.userDescription}</small>
                                                </div>}
                                        </div>))}
                                </Neo.Loader>
                                <div className="mt-3 text-end">
                                    <Neo.Button className="btn-150 mb-3" onClick={() => this.showTradeCalculator()}>Continue</Neo.Button>
                                </div>
                            </>}
                    </div>}

                <div className="mt-4">
                    {<PendingTradeRequestsComponent taskRunner={viewModel.taskRunner} />}
                </div>
            </div>
        )
    }
}